import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, Upload, Button, Switch, message, Tabs } from 'antd';
import { UploadOutlined, SendOutlined, SaveOutlined } from '@ant-design/icons';
// Using native Date instead of dayjs
import { ICreateNotificationRequest, INotification, INotificationTemplate } from '../../common/notification.interface';
import NotificationApi from '../../api/notification/notification.api';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface CModalNotificationProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  notification?: INotification | null;
}

const CModalNotification: React.FC<CModalNotificationProps> = ({
  open,
  onCancel,
  onSubmit,
  notification
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<INotificationTemplate[]>([]);
  const [isScheduled, setIsScheduled] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [testDeviceToken, setTestDeviceToken] = useState('');

  const mode = notification ? 'edit' : 'create';

  useEffect(() => {
    if (open) {
      loadTemplates();
      if (mode === 'edit' && notification) {
        form.setFieldsValue({
          ...notification,
          scheduledTime: notification.scheduledTime ? new Date(notification.scheduledTime) : null,
        });
        setIsScheduled(!!notification.scheduledTime);
      } else {
        form.resetFields();
        setIsScheduled(false);
        setSelectedTemplate('');
      }
    }
  }, [open, notification, mode, form]);

  const loadTemplates = async () => {
    try {
      NotificationApi.getNotificationTemplates().subscribe({
        next: (response) => {
          setTemplates(response);
        },
        error: (error) => {
          console.error('Error loading templates:', error);
        }
      });
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      form.setFieldsValue({
        title: template.title,
        message: template.message,
        type: template.type,
        imageUrl: template.imageUrl,
      });
      setSelectedTemplate(templateId);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const notificationData: ICreateNotificationRequest = {
        title: values.title,
        message: values.message,
        type: values.type,
        targetType: values.targetType,
        targetIds: values.targetType === 'SPECIFIC' ? values.targetIds : undefined,
        vipTierId: values.targetType === 'VIP' ? values.vipTierId : undefined,
        scheduledTime: isScheduled && values.scheduledTime ? values.scheduledTime.toISOString() : undefined,
        imageUrl: values.imageUrl,
        actionUrl: values.actionUrl,
      };

      if (mode === 'create') {
        NotificationApi.createNotification(notificationData).subscribe({
          next: (response) => {
            message.success('Tạo thông báo thành công!');
            onSubmit();
            handleCancel();
          },
          error: (error) => {
            message.error('Có lỗi xảy ra khi tạo thông báo');
            console.error('Error creating notification:', error);
          }
        });
      } else if (mode === 'edit' && notification) {
        NotificationApi.updateNotification(notification.id, notificationData).subscribe({
          next: (response) => {
            message.success('Cập nhật thông báo thành công!');
            onSubmit();
            handleCancel();
          },
          error: (error) => {
            message.error('Có lỗi xảy ra khi cập nhật thông báo');
            console.error('Error updating notification:', error);
          }
        });
      }
    } catch (error) {
      message.error('Có lỗi xảy ra');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!testDeviceToken.trim()) {
      message.error('Vui lòng nhập device token để test');
      return;
    }

    try {
      const values = await form.validateFields();
      setLoading(true);

      const testData = {
        title: values.title,
        message: values.message,
        deviceTokens: [testDeviceToken.trim()],
      };

      NotificationApi.sendTestNotification(testData).subscribe({
        next: (response) => {
          if (response.success) {
            message.success(`Gửi test thành công! Đã gửi tới ${response.sentCount} thiết bị`);
          } else {
            message.error(response.message || 'Gửi test thất bại');
          }
        },
        error: (error) => {
          message.error('Có lỗi xảy ra khi gửi test notification');
          console.error('Error sending test notification:', error);
        }
      });
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsScheduled(false);
    setSelectedTemplate('');
    setTestDeviceToken('');
    onCancel();
  };

  const uploadProps = {
    name: 'file',
    action: 'https://www.mocky.io/v2/5cc8019d300000980a055e76',
    headers: {
      authorization: 'authorization-text',
    },
    onChange(info: any) {
      if (info.file.status !== 'uploading') {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === 'done') {
        message.success(`${info.file.name} file uploaded successfully`);
        form.setFieldsValue({ imageUrl: info.file.response.url });
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  };

  return (
    <Modal
      title={mode === 'create' ? 'Tạo thông báo mới' : 'Chỉnh sửa thông báo'}
      open={open}
      onCancel={handleCancel}
      width={800}
      className="notification-modal"
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Hủy
        </Button>,
        <Button 
          key="test" 
          icon={<SendOutlined />} 
          onClick={handleSendTest}
          loading={loading}
          disabled={mode === 'edit'}
        >
          Gửi Test
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<SaveOutlined />}
          onClick={() => form.submit()}
          loading={loading}
        >
          {mode === 'create' ? 'Tạo mới' : 'Cập nhật'}
        </Button>,
      ]}
    >
      <Tabs defaultActiveKey="1">
        <TabPane tab="Thông tin cơ bản" key="1">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              type: 'INFO',
              targetType: 'ALL',
            }}
          >
            {mode === 'create' && (
              <Form.Item label="Sử dụng template">
                <Select
                  placeholder="Chọn template (tùy chọn)"
                  allowClear
                  value={selectedTemplate}
                  onChange={handleTemplateSelect}
                >
                  {templates.map(template => (
                    <Option key={template.id} value={template.id}>
                      {template.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              name="title"
              label="Tiêu đề"
              rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
            >
              <Input placeholder="Nhập tiêu đề thông báo" />
            </Form.Item>

            <Form.Item
              name="message"
              label="Nội dung"
              rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
            >
              <TextArea rows={4} placeholder="Nhập nội dung thông báo" />
            </Form.Item>

            <Form.Item
              name="type"
              label="Loại thông báo"
              rules={[{ required: true, message: 'Vui lòng chọn loại thông báo' }]}
            >
              <Select>
                <Option value="INFO">Thông tin</Option>
                <Option value="SUCCESS">Thành công</Option>
                <Option value="WARNING">Cảnh báo</Option>
                <Option value="ERROR">Lỗi</Option>
                <Option value="PROMOTION">Khuyến mãi</Option>
                <Option value="SYSTEM">Hệ thống</Option>
                <Option value="BOOK_UPDATE">Cập nhật sách</Option>
                <Option value="ORDER_UPDATE">Cập nhật đơn hàng</Option>
                <Option value="VIP_UPDATE">Cập nhật VIP</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="targetType"
              label="Đối tượng nhận"
              rules={[{ required: true, message: 'Vui lòng chọn đối tượng nhận' }]}
            >
              <Select>
                <Option value="ALL">Tất cả người dùng</Option>
                <Option value="VIP">Người dùng VIP</Option>
                <Option value="SPECIFIC">Người dùng cụ thể</Option>
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.targetType !== currentValues.targetType
              }
            >
              {({ getFieldValue }) => {
                const targetType = getFieldValue('targetType');
                
                if (targetType === 'VIP') {
                  return (
                    <Form.Item
                      name="vipTierId"
                      label="VIP Tier ID"
                      rules={[{ required: true, message: 'Vui lòng nhập VIP Tier ID' }]}
                    >
                      <Input type="number" placeholder="Nhập VIP Tier ID (1, 2, 3...)" />
                    </Form.Item>
                  );
                }

                if (targetType === 'SPECIFIC') {
                  return (
                    <Form.Item
                      name="targetIds"
                      label="ID người dùng"
                      rules={[{ required: true, message: 'Vui lòng nhập ID người dùng' }]}
                    >
                      <Select mode="tags" placeholder="Nhập ID người dùng, phân cách bằng Enter">
                      </Select>
                    </Form.Item>
                  );
                }

                return null;
              }}
            </Form.Item>

            <Form.Item label="Hình ảnh">
              <Form.Item name="imageUrl" noStyle>
                <Input placeholder="URL hình ảnh" />
              </Form.Item>
              <Upload {...uploadProps} showUploadList={false}>
                <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>
                  Tải lên hình ảnh
                </Button>
              </Upload>
            </Form.Item>

            <Form.Item
              name="actionUrl"
              label="URL hành động"
            >
              <Input placeholder="Deep link hoặc URL (tùy chọn)" />
            </Form.Item>

            <Form.Item label="Lên lịch gửi">
              <Switch
                checked={isScheduled}
                onChange={setIsScheduled}
                checkedChildren="Có"
                unCheckedChildren="Không"
              />
            </Form.Item>

            {isScheduled && (
              <Form.Item
                name="scheduledTime"
                label="Thời gian gửi"
                rules={[{ required: true, message: 'Vui lòng chọn thời gian gửi' }]}
              >
                <DatePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  placeholder="Chọn thời gian gửi"
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current.valueOf() < Date.now()}
                />
              </Form.Item>
            )}
          </Form>
        </TabPane>

        {mode === 'create' && (
          <TabPane tab="Test notification" key="2">
            <div>
              <h4>Gửi test notification</h4>
              <p>Nhập device token để test gửi thông báo trước khi gửi chính thức.</p>
              
              <Input.TextArea
                rows={3}
                placeholder="Nhập device token của thiết bị test..."
                value={testDeviceToken}
                onChange={(e) => setTestDeviceToken(e.target.value)}
                style={{ marginBottom: 16 }}
              />
              
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendTest}
                loading={loading}
                block
              >
                Gửi Test Notification
              </Button>
            </div>
          </TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

export default CModalNotification;
